import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/hooks/useSEO';
import { useAuth } from '@/hooks/useClerkAuth';
import {
  fetchQuestionCatalogWithQuestions,
  fetchQuestionsForPractice,
} from '@/services/questionCatalogs';
import {
  QuestionCatalogWithQuestions,
  Question,
} from '@/services/questionCatalogs';
import {
  getOrCreatePracticeProgress,
  updatePracticeProgress,
  resetPracticeProgress,
  PracticeProgress,
  getProgressStats,
} from '@/services/practiceProgress';
import {
  canPracticeQuestions,
  recordQuestionPractice,
  FREE_QUESTIONS_PER_CATALOG,
  DURATION_MINUTES,
} from '@/services/questionUsage';
import QuestionReader, {
  QuestionWithAnswers,
} from '@/components/QuestionReader';
import UsageIndicator from '@/components/UsageIndicator';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Play,
  Settings,
  ArrowLeft,
  Shuffle,
  List,
  Target,
  Users,
  RotateCcw,
  Clock,
  AlertCircle,
  Crown,
  Zap,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  canAnswerQuestion,
  consumeQuestion as consumeTrialQuestion,
  getRemainingTrialQuestions,
  TRIAL_QUESTIONS_LIMIT,
} from '@/services/trialTracker';

// German states for state-specific questions
const GERMAN_STATES = [
  'Baden-Württemberg',
  'Bayern',
  'Berlin',
  'Brandenburg',
  'Bremen',
  'Hamburg',
  'Hessen',
  'Mecklenburg-Vorpommern',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thüringen',
];

const QuestionPractice = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();
  const navigate = useNavigate();
  const { user, isLoading, hasProAccess, hasFreeAccess } = useAuth();
  const { toast } = useToast();

  const [catalog, setCatalog] = useState<QuestionCatalogWithQuestions | null>(
    null
  );
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [practiceMode, setPracticeMode] = useState<'sequential' | 'random'>(
    'sequential'
  );
  const [selectedState, setSelectedState] = useState<string>('');
  const [showSettings, setShowSettings] = useState(true);
  const [isStarted, setIsStarted] = useState(false);
  const [practiceProgress, setPracticeProgress] =
    useState<PracticeProgress | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [startFromBeginning, setStartFromBeginning] = useState(false);

  // Rate limiting state
  const [canPractice, setCanPractice] = useState(true);
  const [remainingQuestions, setRemainingQuestions] = useState<number>(0);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [remainingTrialQuestions, setRemainingTrialQuestions] = useState(
    getRemainingTrialQuestions()
  );
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);

  // No longer redirect unauthenticated users - allow trial access
  // Redirect only happens when trial limit is reached

  // Load catalog data
  useEffect(() => {
    if (!catalogId || isLoading) return;

    const loadCatalog = async () => {
      try {
        setLoadingCatalog(true);
        const fetchedCatalog =
          await fetchQuestionCatalogWithQuestions(catalogId);
        if (fetchedCatalog) {
          setCatalog(fetchedCatalog);
        } else {
          navigate('/questions');
        }
      } catch (error) {
        console.error('Error loading question catalog:', error);
        navigate('/questions');
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
  }, [catalogId, isLoading, navigate]);

  // Helper function to format duration text
  const formatDuration = () => {
    if (DURATION_MINUTES < 60) {
      return t('questionPractice.duration.minutes', {
        count: DURATION_MINUTES,
      });
    } else if (DURATION_MINUTES < 24 * 60) {
      const hours = Math.floor(DURATION_MINUTES / 60);
      return t('questionPractice.duration.hours', { count: hours });
    } else {
      const days = Math.floor(DURATION_MINUTES / (24 * 60));
      return t('questionPractice.duration.days', { count: days });
    }
  };

  // Check rate limits when user and catalog are loaded
  const checkRateLimits = useCallback(async () => {
    if (!catalogId) return;

    // Handle authenticated users
    if (user?.id) {
      try {
        const usageResult = await canPracticeQuestions(
          user.id,
          hasProAccess,
          catalogId
        );
        setCanPractice(usageResult.allowed);
        setRemainingQuestions(usageResult.remainingQuestions);
        setRateLimitError(usageResult.error || null);
      } catch (error) {
        console.error('Error checking rate limits:', error);
        setRateLimitError(t('questionPractice.usageTrackingError'));
      }
    } else {
      // Handle trial users
      const trialRemaining = getRemainingTrialQuestions();
      setRemainingTrialQuestions(trialRemaining);
      setCanPractice(trialRemaining > 0);
      if (trialRemaining === 0) {
        setRateLimitError(
          t('questionPractice.trialLimitReached', {
            limit: TRIAL_QUESTIONS_LIMIT,
          })
        );
      } else {
        setRateLimitError(null);
      }
    }
  }, [user?.id, catalogId, hasProAccess, t]);

  const getQuestionCount = useCallback(() => {
    if (!catalog) return 0;
    let count = catalog.totalQuestions;
    if (selectedState && catalog.stateSpecificQuestions > 0) {
      count = catalog.totalQuestions - catalog.stateSpecificQuestions + 10;
    }
    return count;
  }, [catalog, selectedState]);

  useEffect(() => {
    checkRateLimits();
  }, [checkRateLimits]);

  // Load practice progress when catalog and settings are ready
  useEffect(() => {
    if (!catalog || !user || practiceMode !== 'sequential') {
      setPracticeProgress(null);
      return;
    }

    const loadProgress = async () => {
      if (!user?.id) return;

      setLoadingProgress(true);
      const totalQuestions = getQuestionCount();
      const progress = await getOrCreatePracticeProgress(
        user.id,
        catalog.id,
        selectedState || null,
        totalQuestions
      );
      setPracticeProgress(progress);
      setLoadingProgress(false);
    };

    loadProgress();
  }, [catalog, user, practiceMode, selectedState, getQuestionCount]);

  const handleStartPractice = async () => {
    if (!catalog) return;

    try {
      // Handle progress reset if requested
      if (
        startFromBeginning &&
        practiceProgress &&
        practiceMode === 'sequential'
      ) {
        await resetPracticeProgress(user.id, catalog.id, selectedState || null);
        // Reload progress
        const totalQuestions = getQuestionCount();
        const resetProgress = await getOrCreatePracticeProgress(
          user.id,
          catalog.id,
          selectedState || null,
          totalQuestions
        );
        setPracticeProgress(resetProgress);
      }

      const fetchedQuestions = await fetchQuestionsForPractice(
        catalog.id,
        selectedState || null,
        practiceMode === 'random'
      );

      if (fetchedQuestions.length === 0) {
        // No questions available with current settings
        return;
      }

      setQuestions(fetchedQuestions);
      setIsStarted(true);
      setShowSettings(false);
    } catch (error) {
      console.error('Error starting practice:', error);
    }
  };

  const handleAnswerSubmit = async (
    questionId: string,
    selectedAnswerId: string,
    isCorrect: boolean,
    questionNumber: number
  ) => {
    // Handle authenticated users
    if (user?.id && catalogId) {
      // For free users, check rate limit BEFORE allowing submission
      if (!hasProAccess) {
        // Check if user still has questions remaining
        if (remainingQuestions <= 0) {
          toast({
            title: t('questionPractice.questionLimitReached'),
            description: t('questionPractice.questionLimitReachedDesc', {
              limit: FREE_QUESTIONS_PER_CATALOG,
            }),
            variant: 'destructive',
          });
          return; // Prevent submission
        }
      }

      // Record question practice for rate limiting
      try {
        const success = await recordQuestionPractice(
          user.id,
          hasProAccess,
          catalogId
        );

        if (success) {
          // Refresh rate limit status to get updated counts
          await checkRateLimits();

          // Show warning if approaching limit
          if (
            !hasProAccess &&
            remainingQuestions <= 3 &&
            remainingQuestions > 1
          ) {
            toast({
              title: t('questionPractice.approachingLimit'),
              description: t('questionPractice.onlyRemaining', {
                count: remainingQuestions - 1,
              }),
              variant: 'default',
            });
          } else if (!hasProAccess && remainingQuestions <= 1) {
            toast({
              title: t('questionPractice.questionLimitReached'),
              description: t('questionPractice.questionLimitReachedDesc', {
                limit: FREE_QUESTIONS_PER_CATALOG,
              }),
              variant: 'destructive',
            });
          }
        } else {
          toast({
            title: t('questionPractice.usageTrackingFailed'),
            description: t('questionPractice.usageTrackingFailedDesc'),
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error recording question practice:', error);
        toast({
          title: t('questionPractice.error'),
          description: t('questionPractice.usageTrackingError'),
          variant: 'destructive',
        });
      }
    } else {
      // Handle trial users
      if (!canAnswerQuestion()) {
        setShowTrialPrompt(true);
        return; // Prevent submission
      }

      // Consume a trial question
      const updatedUsage = consumeTrialQuestion();
      const newRemainingTrial =
        TRIAL_QUESTIONS_LIMIT - updatedUsage.questionsAnswered;
      setRemainingTrialQuestions(newRemainingTrial);

      // Show trial limit reached prompt if no questions left
      if (newRemainingTrial <= 0) {
        setShowTrialPrompt(true);
        return;
      }

      // Show warning if approaching trial limit
      if (newRemainingTrial <= 3 && newRemainingTrial > 0) {
        toast({
          title: t('questionPractice.trialApproachingLimit'),
          description: t('questionPractice.trialOnlyRemaining', {
            count: newRemainingTrial,
          }),
          variant: 'default',
        });
      }
    }

    // Track progress for sequential mode
    if (practiceMode === 'sequential' && catalog && user) {
      try {
        await updatePracticeProgress(
          user.id,
          catalog.id,
          {
            currentQuestionNumber: questionNumber + 1,
            completedQuestionNumber: questionNumber,
          },
          selectedState || null
        );

        // Update local progress state
        if (practiceProgress) {
          const updatedProgress = {
            ...practiceProgress,
            currentQuestionNumber: questionNumber + 1,
            completedQuestions: [
              ...practiceProgress.completedQuestions,
              questionNumber,
            ].sort((a, b) => a - b),
          };
          setPracticeProgress(updatedProgress);
        }
      } catch (error) {
        console.error('Error updating practice progress:', error);
      }
    }

    console.log('Answer submitted:', {
      questionId,
      selectedAnswerId,
      isCorrect,
      questionNumber,
    });
  };

  const handleClosePractice = async () => {
    setIsStarted(false);
    setShowSettings(true);
    // Refresh rate limit status to update practice button availability
    await checkRateLimits();
  };

  const convertQuestionsToReaderFormat = (
    questions: Question[]
  ): QuestionWithAnswers[] => {
    return questions.map(q => ({
      id: q.id,
      questionNumber: q.questionNumber,
      germanQuestion: q.germanQuestion,
      englishQuestion: q.englishQuestion,
      vietnameseQuestion: q.vietnameseQuestion,
      imageUrl: q.imageUrl,
      answers: q.answers.map(a => ({
        id: a.id,
        answerIndex: a.answerIndex,
        germanAnswer: a.germanAnswer,
        englishAnswer: a.englishAnswer,
        vietnameseAnswer: a.vietnameseAnswer,
        isCorrect: a.isCorrect,
      })),
    }));
  };

  if (isLoading || loadingCatalog) {
    return (
      <div className="min-h-screen bg-gradient-hero">
        <div className="container max-w-4xl mx-auto p-6">
          <div className="space-y-6">
            <Skeleton className="h-12 w-3/4" />
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-12 w-1/3" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!catalog) {
    return null;
  }

  // Trial limit reached prompt
  if (showTrialPrompt) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
        <Card className="max-w-md mx-auto bg-white/95 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-learning-primary" />
              {t('trialLimit.trialComplete')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {t('trialLimit.questionsLimitReached', {
                limit: TRIAL_QUESTIONS_LIMIT,
              })}
            </p>
            <Alert>
              <Crown className="h-4 w-4" />
              <AlertDescription>
                {t('trialLimit.signUpToContinue')}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate('/sign-up')}
                className="bg-learning-primary hover:bg-learning-primary/90"
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('auth.signUpFree')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/sign-in')}>
                {t('auth.signIn')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/questions')}
                className="mt-2"
              >
                {t('questionPractice.back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show practice session
  if (isStarted && questions.length > 0) {
    return (
      <QuestionReader
        questions={convertQuestionsToReaderFormat(questions)}
        onClose={handleClosePractice}
        catalogName={catalog.name}
        catalogId={catalogId}
        isPracticeMode={true}
        onAnswerSubmit={handleAnswerSubmit}
        startingIndex={
          practiceMode === 'sequential' &&
          practiceProgress &&
          !startFromBeginning
            ? Math.max(0, practiceProgress.currentQuestionNumber - 1)
            : 0
        }
        hasProAccess={hasProAccess}
        remainingQuestions={remainingQuestions}
        remainingTrialQuestions={remainingTrialQuestions}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      <SEO
        title={`${catalog.name} – Practice Test`}
        description={
          catalog.description
            ? `${catalog.description} Practice ${catalog.totalQuestions} questions with English and Vietnamese translations.`
            : `Practice the ${catalog.name} test with ${catalog.totalQuestions} questions. Includes instant translations and detailed explanations.`
        }
        canonicalPath={`/questions/${catalogId}/practice`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Quiz',
          name: catalog.name,
          description:
            catalog.description ||
            `Practice test for ${catalog.name} with ${catalog.totalQuestions} questions.`,
          url: `https://linguacraft.de/questions/${catalogId}/practice`,
          numberOfQuestions: catalog.totalQuestions,
          educationalLevel: 'beginner',
          inLanguage: 'de',
          provider: {
            '@type': 'Organization',
            name: 'Lingua Craft',
            url: 'https://linguacraft.de',
          },
        }}
      />
      <div className="container max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/questions')}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('questionPractice.back')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-primary-foreground">
              {catalog.name}
            </h1>
            <p className="text-primary-foreground/90">
              {t('questionPractice.practiceMode')}
            </p>
          </div>
        </div>

        {showSettings && (
          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                {t('questionPractice.practiceSettings')}
              </CardTitle>
              <CardDescription>
                {t('questionPractice.practiceSettingsDesc')}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Usage Indicator - only for authenticated users */}
              {catalogId && user && <UsageIndicator catalogId={catalogId} />}

              {/* Trial Status Indicator - only for unauthenticated users */}
              {!user && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-blue-900">
                    <Crown className="h-4 w-4" />
                    {t('questionPractice.trialMode')}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-800">
                        {t('questionPractice.trialQuestionsRemaining')}
                      </span>
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800"
                      >
                        {remainingTrialQuestions} / {TRIAL_QUESTIONS_LIMIT}
                      </Badge>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(remainingTrialQuestions / TRIAL_QUESTIONS_LIMIT) * 100}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-blue-700">
                      {t('questionPractice.trialModeDescription')}
                    </p>
                  </div>
                </div>
              )}

              {/* Billing and Rate Limit Warnings */}
              {rateLimitError && !hasProAccess && hasFreeAccess && user && (
                <Alert
                  variant="destructive"
                  className="border-orange-200 bg-orange-50"
                >
                  <Crown className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    <div className="space-y-3">
                      <p className="font-medium">
                        {t('questionPractice.freePlanLimitReached')}
                      </p>
                      <p className="text-sm">{rateLimitError}</p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => navigate('/pricing')}
                          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                        >
                          <Zap className="h-3 w-3 mr-1" />
                          {t('questionPractice.upgradeToPro')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/questions')}
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        >
                          {t('questionPractice.browseOtherCatalogs')}
                        </Button>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {rateLimitError && hasProAccess && user && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{rateLimitError}</AlertDescription>
                </Alert>
              )}

              {/* Trial Limit Warning for Unauthenticated Users */}
              {rateLimitError && !user && (
                <Alert
                  variant="destructive"
                  className="border-orange-200 bg-orange-50"
                >
                  <Crown className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    <div className="space-y-3">
                      <p className="font-medium">
                        {t('questionPractice.trialLimitReachedTitle')}
                      </p>
                      <p className="text-sm">{rateLimitError}</p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => navigate('/sign-up')}
                          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                        >
                          <Crown className="h-3 w-3 mr-1" />
                          {t('auth.signUpFree')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/sign-in')}
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        >
                          {t('auth.signIn')}
                        </Button>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Free Plan Benefits Display */}
              {hasFreeAccess && !hasProAccess && !rateLimitError && user && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Crown className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {t('questionPractice.freePlan')}
                        </p>
                        <p className="text-sm">
                          {remainingQuestions > 0
                            ? t('questionPractice.questionsRemainingThis', {
                                count: remainingQuestions,
                                duration: formatDuration(),
                              })
                            : t('questionPractice.limitedTo', {
                                limit: FREE_QUESTIONS_PER_CATALOG,
                                duration: formatDuration(),
                              })}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/pricing')}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        {t('questionPractice.upgrade')}
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Pro Plan Benefits Display */}
              {hasProAccess && (
                <Alert className="border-green-200 bg-green-50">
                  <Crown className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          {t('questionPractice.proActive')}
                        </p>
                        <p className="text-sm">
                          {t('questionPractice.proBenefits')}
                        </p>
                      </div>
                      <Badge
                        variant="secondary"
                        className="bg-green-100 text-green-800"
                      >
                        {t('questionPractice.unlimited')}
                      </Badge>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Progress Display for Sequential Mode */}
              {practiceMode === 'sequential' &&
                practiceProgress &&
                !loadingProgress && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2 text-blue-900">
                      <Clock className="h-4 w-4" />
                      {t('questionPractice.yourProgress')}
                    </h4>
                    <div className="space-y-2">
                      {(() => {
                        const stats = getProgressStats(practiceProgress);
                        return (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-blue-800">
                                {t('questionPractice.completed')}
                              </span>
                              <Badge
                                variant="secondary"
                                className="bg-blue-100 text-blue-800"
                              >
                                {t('questionReader.completedOf', {
                                  completed: stats.completedCount,
                                  total: stats.totalQuestions,
                                })}
                              </Badge>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-blue-800">
                                {t('questionPractice.current')}
                              </span>
                              <Badge
                                variant="outline"
                                className="border-blue-300 text-blue-800"
                              >
                                {t('questionReader.questionNumber', {
                                  number: stats.currentQuestionNumber,
                                })}
                              </Badge>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{
                                  width: `${stats.completionPercentage}%`,
                                }}
                              />
                            </div>
                            <p className="text-xs text-blue-700">
                              {stats.completionPercentage}
                              {t('questionPractice.percentComplete')}
                              {stats.isCompleted &&
                                t('questionPractice.wellDone')}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

              {/* Practice Mode Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  {t('questionPractice.practiceMode')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <Card
                    className={`cursor-pointer transition-all ${
                      practiceMode === 'sequential'
                        ? 'border-learning-primary bg-learning-primary/5'
                        : 'border-border hover:border-learning-primary/40'
                    }`}
                    onClick={() => setPracticeMode('sequential')}
                  >
                    <CardContent className="p-4 text-center">
                      <List className="h-8 w-8 mx-auto mb-2 text-learning-primary" />
                      <h3 className="font-medium mb-1">Sequential</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('questionPractice.questionsInOrder')}
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    className={`cursor-pointer transition-all ${
                      practiceMode === 'random'
                        ? 'border-learning-primary bg-learning-primary/5'
                        : 'border-border hover:border-learning-primary/40'
                    }`}
                    onClick={() => setPracticeMode('random')}
                  >
                    <CardContent className="p-4 text-center">
                      <Shuffle className="h-8 w-8 mx-auto mb-2 text-learning-primary" />
                      <h3 className="font-medium mb-1">
                        {t('questionPractice.random')}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t('questionPractice.randomizedOrder')}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* State Selection (if applicable) */}
              {catalog.stateSpecificQuestions > 0 && (
                <div className="space-y-3">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t('questionPractice.stateOptional')}
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={setSelectedState}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t('questionPractice.selectGermanState')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">
                        {t('questionPractice.generalQuestionsOnly')}
                      </SelectItem>
                      {GERMAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {selectedState
                      ? `Includes ${catalog.totalQuestions - catalog.stateSpecificQuestions} general + 10 ${selectedState}-specific questions`
                      : `${catalog.totalQuestions - catalog.stateSpecificQuestions} general questions only`}
                  </p>
                </div>
              )}

              {/* Practice Summary */}
              <div className="bg-learning-muted/20 rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  {t('questionPractice.practiceSummary')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('questionPractice.totalQuestions')}</span>
                    <Badge variant="secondary">{getQuestionCount()}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('questionPractice.mode')}</span>
                    <Badge variant="outline">
                      {practiceMode === 'sequential'
                        ? t('questionPractice.sequential')
                        : t('questionPractice.random')}
                    </Badge>
                  </div>
                  {selectedState && (
                    <div className="flex justify-between">
                      <span>{t('questionPractice.state')}</span>
                      <Badge variant="outline">{selectedState}</Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Start/Resume Buttons */}
              {practiceMode === 'sequential' &&
              practiceProgress &&
              !loadingProgress ? (
                <div className="space-y-3">
                  {(() => {
                    const stats = getProgressStats(practiceProgress);
                    const hasProgress =
                      stats.currentQuestionNumber > 1 ||
                      stats.completedCount > 0;

                    if (hasProgress && !stats.isCompleted) {
                      return (
                        <>
                          <Button
                            onClick={() => {
                              if (canPractice) {
                                setStartFromBeginning(false);
                                handleStartPractice();
                              } else if (!user) {
                                navigate('/sign-up');
                              } else if (!hasProAccess && hasFreeAccess) {
                                navigate('/pricing');
                              }
                            }}
                            size="lg"
                            className={`w-full ${
                              canPractice
                                ? 'bg-learning-primary hover:bg-learning-primary/90'
                                : !user || !hasProAccess
                                  ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                                  : 'bg-gray-400'
                            }`}
                            disabled={!canPractice && hasProAccess}
                          >
                            {canPractice ? (
                              <Play className="h-5 w-5 mr-2" />
                            ) : !user || !hasProAccess ? (
                              <Crown className="h-5 w-5 mr-2" />
                            ) : (
                              <Play className="h-5 w-5 mr-2" />
                            )}
                            {canPractice
                              ? `${t('questionPractice.continueFromQuestion')} ${stats.currentQuestionNumber}`
                              : !user
                                ? t('trialLimit.signUpToContinue')
                                : hasProAccess
                                  ? t('questionPractice.limitReached')
                                  : t('questionPractice.upgradeToContinue')}
                          </Button>
                          <Button
                            onClick={() => {
                              if (canPractice) {
                                setStartFromBeginning(true);
                                handleStartPractice();
                              } else if (!user) {
                                navigate('/sign-up');
                              } else if (!hasProAccess && hasFreeAccess) {
                                navigate('/pricing');
                              }
                            }}
                            variant="outline"
                            size="lg"
                            className={`w-full ${
                              !canPractice && (!user || !hasProAccess)
                                ? 'border-orange-200 text-orange-600 hover:bg-orange-50'
                                : ''
                            }`}
                            disabled={!canPractice && hasProAccess}
                          >
                            {canPractice ? (
                              <RotateCcw className="h-5 w-5 mr-2" />
                            ) : !user || !hasProAccess ? (
                              <Crown className="h-5 w-5 mr-2" />
                            ) : (
                              <RotateCcw className="h-5 w-5 mr-2" />
                            )}
                            {canPractice
                              ? t('questionPractice.startFromBeginning')
                              : !user
                                ? t('trialLimit.signUpToContinue')
                                : hasProAccess
                                  ? t('questionPractice.limitReached')
                                  : t('questionPractice.upgradeToStartOver')}
                          </Button>
                        </>
                      );
                    } else if (stats.isCompleted) {
                      return (
                        <Button
                          onClick={() => {
                            if (canPractice) {
                              setStartFromBeginning(true);
                              handleStartPractice();
                            } else if (!user) {
                              navigate('/sign-up');
                            } else if (!hasProAccess && hasFreeAccess) {
                              navigate('/pricing');
                            }
                          }}
                          size="lg"
                          className={`w-full ${
                            canPractice
                              ? 'bg-learning-primary hover:bg-learning-primary/90'
                              : !user || !hasProAccess
                                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                                : 'bg-gray-400'
                          }`}
                          disabled={!canPractice && hasProAccess}
                        >
                          {canPractice ? (
                            <RotateCcw className="h-5 w-5 mr-2" />
                          ) : !user || !hasProAccess ? (
                            <Crown className="h-5 w-5 mr-2" />
                          ) : (
                            <RotateCcw className="h-5 w-5 mr-2" />
                          )}
                          {canPractice
                            ? t('questionPractice.practiceAgain')
                            : !user
                              ? t('trialLimit.signUpToContinue')
                              : hasProAccess
                                ? t('questionPractice.limitReached')
                                : t('questionPractice.upgradeToPractice')}
                        </Button>
                      );
                    } else {
                      return (
                        <Button
                          onClick={() => {
                            if (canPractice) {
                              setStartFromBeginning(false);
                              handleStartPractice();
                            } else if (!user) {
                              navigate('/sign-up');
                            } else if (!hasProAccess && hasFreeAccess) {
                              navigate('/pricing');
                            }
                          }}
                          size="lg"
                          className={`w-full ${
                            canPractice
                              ? 'bg-learning-primary hover:bg-learning-primary/90'
                              : !user || !hasProAccess
                                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                                : 'bg-gray-400'
                          }`}
                          disabled={!canPractice && hasProAccess}
                        >
                          {canPractice ? (
                            <Play className="h-5 w-5 mr-2" />
                          ) : !user || !hasProAccess ? (
                            <Crown className="h-5 w-5 mr-2" />
                          ) : (
                            <Play className="h-5 w-5 mr-2" />
                          )}
                          {canPractice
                            ? t('questionPractice.startPracticeSession')
                            : !user
                              ? t('trialLimit.signUpToContinue')
                              : hasProAccess
                                ? t('questionPractice.limitReached')
                                : t('questionPractice.upgradeToContinue')}
                        </Button>
                      );
                    }
                  })()}
                </div>
              ) : (
                <Button
                  onClick={() => {
                    if (canPractice) {
                      setStartFromBeginning(false);
                      handleStartPractice();
                    } else if (!user) {
                      navigate('/sign-up');
                    } else if (!hasProAccess && hasFreeAccess) {
                      navigate('/pricing');
                    }
                  }}
                  size="lg"
                  className={`w-full ${
                    canPractice
                      ? 'bg-learning-primary hover:bg-learning-primary/90'
                      : !user || !hasProAccess
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                        : 'bg-gray-400'
                  }`}
                  disabled={!canPractice && hasProAccess}
                >
                  {canPractice ? (
                    <Play className="h-5 w-5 mr-2" />
                  ) : !user || !hasProAccess ? (
                    <Crown className="h-5 w-5 mr-2" />
                  ) : (
                    <Play className="h-5 w-5 mr-2" />
                  )}
                  {canPractice
                    ? t('questionPractice.startPracticeSession')
                    : !user
                      ? t('trialLimit.signUpToContinue')
                      : hasProAccess
                        ? t('questionPractice.limitReached')
                        : t('questionPractice.upgradeToContinue')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default QuestionPractice;
